import { ChangeEvent, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { ErrorMessage, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';

import { formatTelephoneNumber, isUserHasKey, scrollIntoViewHelper } from '../GlobalFunctions';
import { updateAllFields, UpdateUserProfile } from '../UserManagement';
import { updateUserProfile } from '../BackendInterface';

import { GradeDropdown, RoleDropdown, SalutationDropdown } from './UserProfileDropdowns';
import { SpinnerOverlay } from '../SpinnerOverlay';
import TextInput from '../components/TextInput';
import EmailOptin from '../components/EmailOptin';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';

import styles from './AccountSettingsForms.module.scss';
import { AppDispatch, RootState } from '../ApplicationState';

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateUserProfile: (partialUserProfile: UpdateUserProfile) => dispatch(updateAllFields(partialUserProfile)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface ProfileFormValues {
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  grade: string;
  schoolName: string;
  tel: string;
  schoolCity: string;
  schoolState: string;
  country: string;
  schoolZip: string;
  optIn: boolean;
}

const AccoutSettingsProfileSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  role: Yup.string().required('Required'),
  schoolName: Yup.string().required('Required'),
  schoolCity: Yup.string().required('Required'),
  country: Yup.string().required('Please select a country'),
  schoolState: Yup.string().min(2, 'Please select a valid region').required('Required'),
});

/* eslint-disable complexity */
//TO DO: REVIEW FUNCTION BELOW FOR COMPLEXITY, CAUSED BY initialProfileValues
const Profile = (props: PropsFromRedux) => {
  const [isShowSpinner, setShowSpinner] = useState<boolean>(false);
  const [isShowSuccess, setShowSuccess] = useState<boolean>(false);
  const [isShowError, setShowError] = useState<boolean>(false);
  const [apiResponse, setApiResponse] = useState<string>('');

  if (!props.userProfile) {
    return null;
  }

  const {
    salutation,
    firstName,
    lastName,
    email,
    role,
    grade,
    schoolZip,
    schoolName,
    tel,
    schoolCity,
    schoolState,
    country,
    optIn,
  } = props.userProfile;

  const isGradeLevelLocked = !isUserHasKey('KEY_CHANGE_GRADE_LEVEL');

  const initialProfileValues: ProfileFormValues = {
    salutation: salutation ?? '',
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    email: email ?? '',
    role: role ?? '',
    grade: isGradeLevelLocked ? 'none' : (grade?.toString() ?? ''),
    schoolZip: schoolZip ?? '',
    schoolName: schoolName ?? '',
    tel: formatTelephoneNumber(tel ?? '', country ?? ''),
    schoolCity: schoolCity ?? '',
    schoolState: schoolState ?? '',
    country: country ?? '',
    optIn: optIn ?? false,
  };

  const handleSubmit = async (values: ProfileFormValues, { setSubmitting }: FormikHelpers<ProfileFormValues>) => {
    if (!props.userProfile) {
      return;
    }

    setShowSpinner(true);
    const requestData = {
      email: values.email,
      salutation: values.salutation,
      firstName: values.firstName,
      lastName: values.lastName,
      role: values.role,
      country: values.country,
      schoolZip: values.schoolZip,
      schoolName: values.schoolName,
      grade: parseInt(values.grade, 10),
      optIn: values.optIn,
      tel: values.tel,
      schoolCity: values.schoolCity,
      schoolState: values.schoolState,
    };
    try {
      await updateUserProfile({ newUserData: requestData });
      props.updateUserProfile({ ...props.userProfile, ...requestData });
      setShowSpinner(false);
      setShowSuccess(true);
    } catch (error) {
      if (error instanceof Error) {
        setApiResponse(error.message);
      } else {
        setApiResponse('Unknown error');
      }
      setShowSpinner(false);
      setShowError(true);
    }
    setSubmitting(false);
  };

  return (
    <section className={styles.container}>
      <SpinnerOverlay isShow={isShowSpinner} />
      <Formik
        initialValues={initialProfileValues}
        onSubmit={handleSubmit}
        validationSchema={AccoutSettingsProfileSchema}
      >
        {({ handleChange, handleBlur, setValues, values, handleSubmit, errors }) => (
          <Form onSubmit={handleSubmit} className={styles.formContainer}>
            <div className={styles.halfRow}>
              <div>
                <SalutationDropdown name='salutation' />
              </div>
              <TextInput label='First Name' name='firstName' type='text' placeholder='Bill' />
            </div>
            <div className={styles.halfRow}>
              <TextInput label='Last Name' name='lastName' type='text' placeholder='Bison' />
            </div>
            <div className={styles.fullRow}>
              <p className={styles.label}>Email</p>
              <p className={styles.text}>{email}</p>
            </div>
            <div className={styles.halfRow}>
              <RoleDropdown name='role' label='Role' />
            </div>
            <div className={styles.halfRow}>
              <GradeDropdown isGradeLevelLocked={isGradeLevelLocked} name='grade' />
            </div>
            <div className={styles.halfRow}>
              <TextInput label='School Name' name='schoolName' type='text' placeholder='Peekaville Elementary School' />
            </div>
            <div className={styles.halfRow}>
              <TextInput
                label='Telephone'
                name='tel'
                type='text'
                placeholder='+1'
                value={formatTelephoneNumber(values.tel, values.country)}
              />
            </div>
            <div className={styles.halfRow}>
              <TextInput label='City' name='schoolCity' type='text' placeholder='Peekaville' />
            </div>
            <div className={styles.halfRow}>
              <div className={styles.countryRegionContainer}>
                <label className={styles.countryRegionDropdownLabel}>State / Province</label>
                <RegionDropdown
                  classes={styles.countryRegionDropdown}
                  name='schoolState'
                  country={values.country}
                  value={values.schoolState}
                  onChange={(_, e) => handleChange(e)}
                />
                <div className={styles.errorHint}>
                  <ErrorMessage name='schoolState' />
                </div>
              </div>
            </div>
            <div className={styles.halfRow}>
              <div className={styles.countryRegionContainer}>
                <label className={styles.countryRegionDropdownLabel}>Country</label>
                <CountryDropdown
                  classes={styles.countryRegionDropdown}
                  name='country'
                  value={values.country}
                  onBlur={handleBlur}
                  onChange={(_, e) => {
                    setValues(
                      (prevValues) => ({
                        ...prevValues,
                        [(e as ChangeEvent<HTMLSelectElement>).currentTarget.name]: (
                          e as ChangeEvent<HTMLSelectElement>
                        ).currentTarget.value,
                        schoolState: '',
                        tel: formatTelephoneNumber(values.tel, values.country),
                      }),
                      true,
                    );
                  }}
                />
                <div className={styles.errorHint}>
                  <ErrorMessage name='country' />
                </div>
              </div>
            </div>
            <div className={styles.halfRow}>
              <TextInput label='Zip / Postal Code' name='schoolZip' type='text' placeholder='12345' />
            </div>
            <div className={styles.fullRow}>
              <EmailOptin name='optIn' onChange={handleChange} defaultChecked={optIn} />
            </div>
            <div className={styles.buttonRow}>
              <PrimaryButton
                small
                className={styles.updateButton}
                type='submit'
                onClick={() => scrollIntoViewHelper(errors)}
              >
                Update Profile
              </PrimaryButton>
              <div
                className={isShowSuccess ? styles.successShown : styles.successHidden}
                onTransitionEnd={() => setShowSuccess(false)}
              >
                Nice! Your account has been successfully updated.
              </div>
              <div
                className={isShowError ? styles.errorShown : styles.errorHidden}
                onTransitionEnd={() => setShowError(false)}
              >
                {apiResponse}
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
};
/* eslint-enable complexity */

export default connector(Profile);
